import React, { useState, useEffect } from 'react';
import { Drawer, Button, Input, Avatar, List, Typography } from 'antd';
import { SendOutlined, UserOutlined, LoadingOutlined } from '@ant-design/icons';
import '../assets/styles/ChatSupportDrawer.css';

const { TextArea } = Input;
const { Text } = Typography;

// Pool of predefined messages for simulation
const messagePool = [
  { name: 'Alice', message: 'Hey everyone, excited to be here! Anyone into AI?', timestamp: 'Just now' },
  { name: 'Bob', message: 'Welcome! I’m working on a machine learning project—tips?', timestamp: 'Just now' },
  { name: 'Charlie', message: 'Hi all, any cybersecurity resources to share?', timestamp: 'Just now' },
  { name: 'Dana', message: 'Just finished a data science course—loving it!', timestamp: 'Just now' },
  { name: 'Eve', message: 'Anyone up for an AI hackathon this weekend?', timestamp: 'Just now' },
  { name: 'Frank', message: 'How’s everyone finding the mentorship program?', timestamp: 'Just now' },
  { name: 'Grace', message: 'Just deployed an AI model—super exciting!', timestamp: 'Just now' },
  { name: 'Hank', message: 'Looking for teammates for an open-source AI project!', timestamp: 'Just now' },
];

// Sample initial messages
const initialMessages = [
  { id: 1, name: 'Alice', message: 'Hey everyone, excited to be here!', timestamp: '10:30 AM' },
  { id: 2, name: 'Bob', message: 'Welcome, Alice! Any AI projects you’re working on?', timestamp: '10:32 AM' },
];

const ChatSupportDrawer = ({ visible, onClose }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState(initialMessages);
  const [isTyping, setIsTyping] = useState(false); // Simulate someone typing
  const [hasNewMessage, setHasNewMessage] = useState(false); // Track new incoming messages

  // Simulate incoming messages
  useEffect(() => {
    if (!visible) return; // Only run when drawer is open

    const interval = setInterval(() => {
      if (Math.random() > 0.5) {
        setIsTyping(true);
        setTimeout(() => {
          const randomMessage = messagePool[Math.floor(Math.random() * messagePool.length)];
          const newMessage = {
            id: messages.length + 1,
            name: randomMessage.name,
            message: randomMessage.message,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          };
          setMessages((prev) => [...prev, newMessage]);
          setHasNewMessage(true); // Trigger notification dot
          setIsTyping(false);
        }, 1500); // Delay to simulate typing
      }
    }, 3000); // New message every 3-5 seconds

    return () => clearInterval(interval); // Cleanup on unmount or drawer close
  }, [visible, messages.length]);

  // Handle sending a message from the user
  const handleSendMessage = () => {
    if (message.trim()) {
      const newMessage = {
        id: messages.length + 1,
        name: 'You',
        message: message.trim(),
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages([...messages, newMessage]);
      setMessage('');
      setHasNewMessage(false); // Clear notification when user interacts

      // Simulate a reply from another participant
      setTimeout(() => {
        const reply = {
          id: messages.length + 2,
          name: messagePool[Math.floor(Math.random() * messagePool.length)].name,
          message: `Cool, @You! ${getRandomReply(message.trim())}`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };
        setMessages((prev) => [...prev, reply]);
        setHasNewMessage(true); // New reply triggers notification again
      }, 2000);
    }
  };

  // Helper function for context-aware replies
  const getRandomReply = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    if (lowerMessage.includes('ai')) return 'I’m also into AI—what’s your favorite framework?';
    if (lowerMessage.includes('cybersecurity')) return 'Check out OWASP for some great resources!';
    if (lowerMessage.includes('data science')) return 'Have you tried Pandas yet? It’s a game-changer!';
    return 'That sounds interesting—tell us more!';
  };

  // Handle clicking "Join Participants Room" (e.g., open WhatsApp or another chat room)
  const handleJoinRoom = () => {
    setHasNewMessage(false); // Clear notification on join
    window.open('https://wa.me/254708031478', '_blank'); // Replace with desired link
  };

  return (
    <Drawer
      title={<span style={{ color: '#1890ff', fontWeight: 'bold' }}>Participants Chat Room</span>}
      placement="right"
      closable
      onClose={onClose}
      open={visible}
      width={400}
      bodyStyle={{ padding: '0', background: '#f5f7fa' }}
      headerStyle={{ background: '#e6f7ff', borderBottom: '1px solid #d9e8ff' }}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Welcome Message */}
        <div style={{ padding: '15px', background: '#fff', borderBottom: '1px solid #e8e8e8', textAlign: 'center' }}>
          <h3 style={{ margin: 0, color: '#1a73e8', fontSize: '18px' }}>
            Welcome to the JectHub Community! 🌟
          </h3>
          <p style={{ margin: '5px 0 0', color: '#595959', fontSize: '14px' }}>
            Chat with fellow participants, share ideas, and grow together!
          </p>
        </div>

        {/* Chat Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '15px' }}>
          <List
            dataSource={messages}
            renderItem={(item) => (
              <List.Item style={{ border: 'none', padding: '10px 0', animation: 'fadeIn 0.3s ease-in' }}>
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        backgroundColor: item.name === 'You' ? '#1890ff' : '#87d068',
                      }}
                      icon={<UserOutlined />}
                    />
                  }
                  title={
                    <span style={{ fontWeight: 'bold', color: '#262626' }}>
                      {item.name}{' '}
                      <span style={{ fontSize: '12px', color: '#8c8c8c' }}>({item.timestamp})</span>
                    </span>
                  }
                  description={<span style={{ color: '#595959' }}>{item.message}</span>}
                />
              </List.Item>
            )}
          />
          {isTyping && (
            <div style={{ padding: '10px', textAlign: 'center', color: '#8c8c8c' }}>
              <LoadingOutlined /> Someone is typing...
            </div>
          )}
        </div>

        {/* Message Input */}
        <div style={{ padding: '15px', background: '#fff', borderTop: '1px solid #e8e8e8' }}>
          <TextArea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{
              width: '100%',
              marginBottom: '10px',
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              padding: '10px',
              fontSize: '14px',
            }}
            onPressEnter={handleSendMessage} // Send on Enter key
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSendMessage}
            style={{
              width: '100%',
              background: '#1890ff',
              borderColor: '#1890ff',
              borderRadius: '6px',
              height: '40px',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
            }}
            disabled={!message.trim()} // Disable if message is empty
            onMouseEnter={(e) => (e.target.style.background = '#40a9ff')}
            onMouseLeave={(e) => (e.target.style.background = '#1890ff')}
          >
            Send Message
          </Button>
          <div style={{ marginTop: '15px', textAlign: 'center' }}>
            <span
              onClick={handleJoinRoom}
              style={{
                color: '#1890ff',
                fontWeight: 'bold',
                fontSize: '14px',
                cursor: 'pointer',
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                transition: 'color 0.3s ease',
              }}
              onMouseEnter={(e) => (e.target.style.color = '#40a9ff')}
              onMouseLeave={(e) => (e.target.style.color = '#1890ff')}
            >
              Join Participants Room
              {hasNewMessage && (
                <span
                  style={{
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#ff4d4f',
                    borderRadius: '50%',
                    marginLeft: '6px',
                    animation: 'pulse 1.5s infinite',
                  }}
                />
              )}
            </span>
            <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#8c8c8c' }}>
              Connect with more participants instantly!
            </p>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx global>{`
        .chat-support-drawer .ant-drawer-body {
          padding: 0 !important;
        }
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>
    </Drawer>
  );
};

export default ChatSupportDrawer;